import React from 'react';
import { generatePath } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { assocPath, path as getByPath, uniq, update } from 'rambdax';
import RouterLink from '@/components/common/RouterLink';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { DonorType } from '@/propTypes';
import { searchForConstituentsByEmail } from '@/services/constituentServices';
import { isValidEmail } from '@/utils/emails';

// TODO: @anna - check if I need arrays here at all
function DonorTextInput({
  containerProps,
  data,
  disabled,
  errorMessage,
  errors,
  idx,
  inputRef,
  label,
  newValueMapper,
  path,
  prefix,
  required,
  setErrors,
  showErrors,
  updateData,
  validate,
  valueMapper,
  width,
  ...rest
}) {
  const [isDirty, setIsDirty] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState(null);
  const [isSearching, setIsSearching] = React.useState(null);
  const ownerOrgId = useOwnerOrgId();

  // check if email address exists in the system
  const checkIfEmailAddressExists = (emailAddress) => {
    return searchForConstituentsByEmail({
      ownerOrgId,
      params: { emailAddress, queryScope: 'email_address' },
    });
  };

  const isArray = typeof idx === 'number';
  const valueByPath = path ? getByPath(path, data) : data;

  // eslint-disable-next-line no-nested-ternary
  const value = isArray
    ? valueByPath?.length
      ? valueByPath[idx]
      : ''
    : valueByPath;

  const [originalValue] = React.useState(value);

  const onChange = React.useCallback(
    ({ target }) => {
      setSearchResults(null);

      const newValue = newValueMapper(target?.value, value, target);
      updateData(
        path
          ? assocPath(
              path,
              isArray
                ? update(
                    idx,
                    newValue,
                    valueByPath?.length ? valueByPath : [''],
                  )
                : newValue,
              data,
            )
          : newValue,
        path,
      );
    },
    [data, errors, idx, valueByPath, updateData],
  );

  const onBlur = React.useCallback(
    async ({ target }) => {
      setIsDirty(true);

      if (
        !(
          target.placeholder.includes(translate('EMAIL_ADDRESSES_LABEL')) &&
          isValidEmail(target?.value)
        )
      ) {
        return;
      }

      // check if the field is for a new constituent (data)
      // or an existing constituent (!data) and the value has changed
      if (data && originalValue === target?.value) return;

      // validate if email address already exists in the system
      try {
        setIsSearching(true);
        const response = await checkIfEmailAddressExists(target?.value);
        if (response?.data?.constituents?.length) {
          setSearchResults(response?.data?.constituents?.[0]);
        }
      } finally {
        setIsSearching(false);
      }
    },
    [data, originalValue],
  );

  const mappedValue = valueMapper(value);
  const isRequiredButEmpty = required && !mappedValue;

  const hasError = React.useMemo(
    () =>
      isRequiredButEmpty ||
      (validate && mappedValue && !validate(mappedValue)) ||
      searchResults,
    [mappedValue, isRequiredButEmpty, searchResults],
  );

  React.useEffect(() => {
    const shouldUpdateErrors =
      (hasError && !errors?.includes(path)) ||
      (!hasError && errors?.includes(path));

    if (shouldUpdateErrors) {
      setErrors(
        hasError
          ? uniq([...errors, path])
          : errors.filter((error) => error !== path),
      );
    }
  }, [hasError]);

  const showError = hasError && (isDirty || showErrors);

  return (
    <Box {...containerProps}>
      <div className="relative w-full">
        {prefix}
        <TextField
          data-testid={`donor-text-input-${path}`}
          disabled={disabled}
          error={Boolean(showError)}
          fullWidth
          inputRef={inputRef}
          InputProps={{
            endAdornment: isSearching && (
              <InputAdornment position="end" sx={{ marginRight: 3 }}>
                <CircularProgress size="24px" />
              </InputAdornment>
            ),
          }}
          label={label}
          onBlur={onBlur}
          onChange={onChange}
          placeholder={label}
          required={required}
          value={mappedValue}
          width={width}
          {...rest}
        />
      </div>
      {showError && !searchResults && (
        <Typography color="error">
          {isRequiredButEmpty ? translate('REQUIRED') : errorMessage}
        </Typography>
      )}
      {showError && searchResults && (
        <Alert icon={false} severity="warning" sx={{ marginTop: 1 }}>
          <Typography>
            {translate('CONSTITUENT_EXISTS_IN_SYSTEM', {
              displayName: searchResults.displayName,
              'link-to-constituent': () => (
                <RouterLink
                  to={generatePath(AuthRoutes.CONSTITUENT, {
                    constituentId: searchResults.constituentId,
                  })}
                >
                  {searchResults.displayName}
                </RouterLink>
              ),
            })}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

DonorTextInput.propTypes = {
  containerProps: PropTypes.shape({}),
  data: DonorType.isRequired,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  idx: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  inputRef: PropTypes.shape({}),
  label: PropTypes.string,
  newValueMapper: PropTypes.func,
  path: PropTypes.string,
  prefix: PropTypes.node,
  required: PropTypes.bool,
  setErrors: PropTypes.func,
  showErrors: PropTypes.bool,
  updateData: PropTypes.func.isRequired,
  validate: PropTypes.func,
  valueMapper: PropTypes.func,
  width: PropTypes.string,
};

DonorTextInput.defaultProps = {
  containerProps: null,
  disabled: false,
  errorMessage: '',
  errors: [],
  idx: null,
  inputRef: null,
  label: '',
  newValueMapper: (value) => value,
  path: '',
  prefix: null,
  required: false,
  setErrors: null,
  showErrors: false,
  validate: null,
  valueMapper: (value) => value || '',
  width: '100%',
};

export default DonorTextInput;
